import { useNavigate } from "@tanstack/react-router";
import type { MerchantDraftPermissionAnswer } from "@unbox/api";
import { Building07, Plus } from "@untitledui/icons";
import { useCallback, useState } from "react";
import { useCreateMerchantDraft, useMerchantDraft, useWorkspaceMerchants } from "@/api/workspace-merchants";
import { Button } from "@/components/base/buttons/button";
import { MerchantAddedModal } from "@/components/workspace-merchants/merchant-added-modal";
import { MerchantInProgressBanner } from "@/components/workspace-merchants/merchant-in-progress-banner";
import { MerchantPermissionModal } from "@/components/workspace-merchants/merchant-permission-modal";
import { WorkspaceMerchantsTable } from "@/components/workspace-merchants/workspace-merchants-table";
import { useWorkspaceId } from "@/providers/MerchantProvider";
import { workspaceMerchantsRoute } from "@/router";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";

export function WorkspaceMerchantsPage() {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  const { added } = workspaceMerchantsRoute.useSearch();
  const closeAddedModal = useCallback(() => {
    if (workspaceId)
      navigate({ to: "/workspace/$workspaceId/settings/merchants", params: { workspaceId }, search: {} });
  }, [navigate, workspaceId]);
  const { data: merchants, isLoading: merchantsLoading } = useWorkspaceMerchants();
  const { data: draft, isLoading: draftLoading } = useMerchantDraft();
  const createDraft = useCreateMerchantDraft();
  const [isAsking, setIsAsking] = useState(false);

  const openWizard = () => {
    if (workspaceId) navigate({ to: "/workspace/$workspaceId/settings/merchants/new", params: { workspaceId } });
  };

  const startDraft = (permissionAnswer: MerchantDraftPermissionAnswer) => {
    createDraft.mutate({ permissionAnswer }, { onSuccess: openWizard });
  };

  const isLoading = merchantsLoading || draftLoading;
  const merchantCount = merchants?.length ?? 0;

  return (
    <div className="flex flex-col w-full h-screen gap-8 items-center overflow-y-auto bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-5 px-6 lg:px-16 pt-16 pb-8`}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-normal text-secondary flex flex-row items-center gap-2 mb-1">
              <Building07 className="w-5 h-5" strokeWidth={1.8} />
              All merchants
            </h1>
            <span className="text-quaternary text-sm">Every merchant connected to your Unbox workspace.</span>
          </div>

          <div className="flex items-center gap-3">
            {!isLoading && (
              <span className="rounded-lg border border-primary bg-primary px-3.5 py-2 text-sm font-semibold text-secondary tabular-nums">
                {merchantCount} {merchantCount === 1 ? "merchant" : "merchants"}
              </span>
            )}
            <Button
              color="primary"
              size="md"
              iconLeading={Plus}
              isDisabled={isLoading || !!draft}
              onClick={() => setIsAsking(true)}
            >
              Add new merchant
            </Button>
          </div>
        </div>

        {draft && (
          <MerchantInProgressBanner
            draft={draft}
            action={
              <Button color="secondary" size="md" onClick={openWizard}>
                Continue adding merchant
              </Button>
            }
          />
        )}

        <div className="rounded-xl border border-primary bg-primary overflow-hidden">
          {isLoading ? (
            <p className="text-sm text-tertiary p-6">Loading merchants...</p>
          ) : (
            <WorkspaceMerchantsTable merchants={merchants ?? []} draft={draft ?? null} />
          )}
        </div>
      </div>

      <MerchantAddedModal isOpen={!!added} onClose={closeAddedModal} />

      <MerchantPermissionModal
        isOpen={isAsking}
        onOpenChange={setIsAsking}
        onContinue={startDraft}
        isPending={createDraft.isPending}
      />
    </div>
  );
}
